/**
 * Statistikk: sykefravær for a month in the official measure, the six months up to it, the
 * absence by kind, this month against last, and a few plain sentences (owner, 2026-09-21). The
 * method is behind "Hvordan beregnes sykefravær?", not on the canvas. The numbers are stats.ts.
 */
import type { ApiAbsence } from '@/api/data';
import { T } from '@/i18n';
import { MONTHS, num } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { Explain } from '@/ui/page';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { CATEGORIES, type Category, catInfo } from './category';
import { CatIcon } from './parts';
import { type MonthStats, insightsOf, monthStats, shiftMonth, trendOf } from './stats';

const pct = (n: number) => `${num(n, 1)} %`;
const monthLabel = (ym: string, short = false) => {
  const m = MONTHS[Number(ym.slice(5, 7)) - 1] ?? '';
  const n = short ? m.slice(0, 3) : m;
  return `${n.charAt(0).toUpperCase()}${n.slice(1)} ${ym.slice(0, 4)}`;
};
/** A bounded section in the manager style: a head with its title, then the content. */
const Card = ({
  title,
  sub,
  flush,
  children,
}: { title: string; sub?: string; flush?: boolean; children: ReactNode }) => (
  <section className="mcard">
    <div className="mch">
      <h2>{title}</h2>
      {sub && <span className="sub">{sub}</span>}
    </div>
    <div className={flush ? '' : 'fcb'}>{children}</div>
  </section>
);

/** A change in percentage points or a count, with its direction; fewer sick days is the good way. */
function Delta({ d, unit }: { d: number; unit: 'pp' | '' }) {
  const r = Math.round(d * 10) / 10;
  if (!r) return <span className="fdelta">{T('uendret')}</span>;
  return (
    <span className={`fdelta ${r < 0 ? 'good' : 'bad'}`}>
      {r < 0 ? '↓' : '↑'} {r > 0 ? '+' : '−'}
      {num(Math.abs(r), 1)}
      {unit ? ` ${unit}` : ''}
    </span>
  );
}

export function Statistikk({
  all,
  people,
  today,
}: {
  all: ApiAbsence[];
  people: ReadonlySet<number>;
  today: string;
}) {
  const [ym, setYm] = useState(today.slice(0, 7));
  const cur = monthStats(ym, all, people);
  const prev = monthStats(shiftMonth(ym, -1), all, people);
  const trend = trendOf(ym, all, people);
  const insights = insightsOf(cur, prev);
  const kinds = CATEGORIES.filter((c) => cur.days[c.key] > 0);
  const maxDays = Math.max(1, ...kinds.map((c) => cur.days[c.key]));

  const kpi = (
    icon: Category | 'sum' | 'cases',
    label: string,
    value: string,
    d: number,
    unit: 'pp' | '',
  ) => (
    <div>
      {icon === 'sum' || icon === 'cases' ? (
        <span className="kic tn-blue">
          <Icon name={icon === 'sum' ? 'percent' : 'users'} />
        </span>
      ) : (
        <CatIcon cat={icon} />
      )}
      <span>
        {T(label)}
        <b>{value}</b>
        <i>
          <Delta d={d} unit={unit} /> {T('fra forrige måned')}
        </i>
      </span>
    </div>
  );

  return (
    <>
      <div className="mbar">
        <label className="mf">
          {T('Måned')}
          <input
            className="inp"
            type="month"
            aria-label={T('Måned')}
            value={ym}
            max={today.slice(0, 7)}
            onChange={(e) => e.target.value && setYm(e.target.value)}
          />
        </label>
        <span className="sp" />
        <span className="mfresh">{T('Innvilget fravær, alle aktive ansatte')}</span>
      </div>

      <div className="kpi fkpi" data-testid="sick-stats">
        {kpi('sum', 'Sykefravær', pct(cur.sick), cur.sick - prev.sick, 'pp')}
        {kpi('egenmelding', 'Egenmelding', pct(cur.egen), cur.egen - prev.egen, 'pp')}
        {kpi('sykemelding', 'Sykemelding', pct(cur.sykm), cur.sykm - prev.sykm, 'pp')}
        {kpi('cases', 'Antall tilfeller', String(cur.cases), cur.cases - prev.cases, '')}
      </div>

      <Explain q={T('Hvordan beregnes sykefravær?')}>
        <div data-testid="sick-method">
          <p>
            {T('Sykefravær er tapte dagsverk i prosent av avtalte dagsverk, slik SSB og NAV regner det.')}
          </p>
          <ul>
            <li>
              {T(
                'Avtalte dagsverk: antall ansatte ganger virkedagene i måneden, mandag til fredag. Alle telles som heltid; stillingsprosent er ikke registrert.',
              )}
            </li>
            <li>
              {T(
                'Tapte dagsverk: virkedagene i innvilget egenmelding og sykemelding innenfor måneden. Sluttdatoen er en fraværsdag.',
              )}
            </li>
            <li>
              {T(
                'Syk barn er omsorgsdager og regnes ikke som sykefravær. Søknader som venter eller er avslått, telles ikke.',
              )}
            </li>
            <li>{T('Et tilfelle er ett innvilget sykefravær med minst én tapt virkedag i måneden.')}</li>
          </ul>
        </div>
      </Explain>

      <div className="fxgrid">
        <Card title={T('Utvikling siste 6 måneder')} sub={T('sykefravær i prosent av avtalte dagsverk')}>
          <Trend months={trend} />
        </Card>
        <Card title={T('Fravær etter type')} sub={`${monthLabel(ym)} · ${T('dagsverk')}`}>
          {kinds.length ? (
            <ul className="fkinds">
              {kinds.map((c) => (
                <li key={c.key}>
                  <CatIcon cat={c.key} />
                  <span className="fkn">
                    {T(c.label)}
                    {c.key === 'syk_barn' && <small>{T('ikke sykefravær')}</small>}
                  </span>
                  <span className={`fkb tn-${catInfo(c.key).tone}`}>
                    <i style={{ width: `${(cur.days[c.key] / maxDays) * 100}%` }} />
                  </span>
                  <span className="fkv">
                    {cur.days[c.key]}{' '}
                    <small>{cur.agreed ? pct((cur.days[c.key] / cur.agreed) * 100) : ''}</small>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="hint" style={{ marginTop: 0 }}>
              {T('Ikke noe innvilget fravær i {month}.', { month: monthLabel(ym).toLowerCase() })}
            </p>
          )}
        </Card>
      </div>

      <div className="fxgrid">
        <Card title={T('Denne måneden mot forrige')} flush>
          <table className="mt">
            <thead>
              <tr>
                <th>{T('Type')}</th>
                <th className="n">{monthLabel(ym, true)}</th>
                <th className="n">{monthLabel(prev.ym, true)}</th>
                <th className="n">{T('Endring')}</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ['Sykefravær (totalt)', cur.sick, prev.sick, 'pp'],
                  ['Sykemelding', cur.sykm, prev.sykm, 'pp'],
                  ['Egenmelding', cur.egen, prev.egen, 'pp'],
                  ['Antall tilfeller', cur.cases, prev.cases, ''],
                ] as const
              ).map(([label, a, b, unit]) => (
                <tr key={label}>
                  <td>{T(label)}</td>
                  <td className="n">{unit ? pct(a) : a}</td>
                  <td className="n">{unit ? pct(b) : b}</td>
                  <td className="n">
                    <Delta d={a - b} unit={unit} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title={T('Kort innsikt')}>
          <ul className="finsight">
            {insights.map((x) => {
              const ic: Record<typeof x.icon, [IconName, string]> = {
                down: ['check', 'tn-ok'],
                up: ['warn', 'tn-warn'],
                flat: ['info', 'tn-slate'],
                cases: ['users', 'tn-blue'],
                share: ['percent', 'tn-violet'],
              };
              return (
                <li key={x.title}>
                  <span className={`kic ${ic[x.icon][1]}`}>
                    <Icon name={ic[x.icon][0]} />
                  </span>
                  <span>
                    <b>{T(x.title)}</b>
                    <small>{x.text}</small>
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </>
  );
}

/** The width of an element, so the chart draws in real pixels and its text stays 12 px on a phone. */
function useWidth<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => e && setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

const SERIES: { key: 'sick' | 'sykm' | 'egen'; label: string; cls: string }[] = [
  { key: 'sick', label: 'Totalt sykefravær', cls: 'l-sum' },
  { key: 'sykm', label: 'Sykemelding', cls: 'l-sykm' },
  { key: 'egen', label: 'Egenmelding', cls: 'l-egen' },
];

/** Three lines over six months, plain SVG; the total carries its values. */
function Trend({ months }: { months: MonthStats[] }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const h = 210;
  const pad = { l: 40, r: 32, t: 22, b: 26 };
  const top = Math.max(4, Math.ceil(Math.max(...months.map((m) => m.sick)) / 2) * 2 + 2);
  const x = (i: number) => pad.l + (i * (width - pad.l - pad.r)) / Math.max(1, months.length - 1);
  const y = (v: number) => pad.t + (1 - v / top) * (h - pad.t - pad.b);
  const ticks = [0, top / 2, top];
  return (
    <div ref={ref} className="ftrend">
      <svg width={width} height={h} role="img" aria-label={T('Sykefravær siste 6 måneder')}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} className="grid" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" className="ax">
              {num(t, 0)} %
            </text>
          </g>
        ))}
        {months.map((m, i) => (
          <text key={m.ym} x={x(i)} y={h - 6} textAnchor="middle" className="ax">
            {monthLabel(m.ym, true)}
          </text>
        ))}
        {SERIES.map((s) => (
          <g key={s.key} className={s.cls}>
            <polyline fill="none" points={months.map((m, i) => `${x(i)},${y(m[s.key])}`).join(' ')} />
            {months.map((m, i) => (
              <circle key={m.ym} cx={x(i)} cy={y(m[s.key])} r={s.key === 'sick' ? 3.5 : 2.5} />
            ))}
            {s.key === 'sick' &&
              months.map((m, i) => (
                <text key={m.ym} x={x(i)} y={y(m.sick) - 8} textAnchor="middle" className="val">
                  {num(m.sick, 1)}
                </text>
              ))}
          </g>
        ))}
      </svg>
      <div className="legend2">
        {SERIES.map((s) => (
          <span key={s.key}>
            <i className={s.cls} />
            {T(s.label)}
          </span>
        ))}
      </div>
    </div>
  );
}
